import { ApolloClient, NormalizedCacheObject, useQuery } from '@apollo/client'
import { useMemo } from 'react'
import { createContainer } from 'unstated-next'

import { gql } from '../../../../__generated__/graphql/catalog'
import {
  GetPdpServerSidePropsQuery,
  PdpServerSideProductFieldsFragment,
  PdpServerSideVariantGroupFieldsFragment,
} from '../../../../__generated__/graphql/catalog/graphql'
import { usePortal } from '../../../../app/_providers/PortalProvider.client'

export const pdpServerSideProductOptionsFields = gql(`
  fragment PdpServerSideProductOptionsFields on ProductCatalogProductOptions {
    colors {
      color {
        id
        hexCodes
        displayValue
        swatchImage {
          url
        }
        group {
          id
          displayValue
        }
      }
      outOfStock
    }
    fits {
      fit {
        id
        displayValue
      }
      outOfStock
    }
    sizes {
      size {
        id
        displayValue
      }
      outOfStock
    }
  }
`)

export const pdpServerSideProductFields = gql(`
  fragment PdpServerSideProductFields on ProductCatalogStandardProduct {
    __typename
    id
    handle
    options(color: $colorKey, fit: $fitKey, size: $sizeKey, collectionHandle: $collectionHandle) {
      ...PdpServerSideProductOptionsFields
    }
    portalOptions: options(color: $colorKey, fit: $fitKey, size: $sizeKey, collectionHandle: $portalCollectionHandle, scope: IN_COLLECTION) @include(if: $isPortalCollectionHandleFilled) {
      ...PdpServerSideProductOptionsFields
    }
  }
`)

export const pdpServerSideVariantGroupFields = gql(`
  fragment PdpServerSideVariantGroupFields on ProductCatalogVariantGroup {
    __typename
    id
    title
    description
    materials
    features
    careInstructions
    fitModelsDescriptions
    associatedCollectionLinks {
      text
      url
    }
    gender
    exclusive
    availability
    stockState
    finalSale
    siteWidePromoEligible
    images(first: 20, fit: $fitKey) {
      url
    }
    urlPath
    primaryCategory {
      id
    }
    priceRange {
      min {
        amount
        currencyCode
      }
      max {
        amount
        currencyCode
      }
    }
    compareAtPriceRange {
      min {
        amount
      }
      max {
        amount
      }
    }
    color {
      group {
        id
        displayValue
      }
      hexCodes
      displayValue
      id
    }
    pdpBadges {
      __typename
      id
      displayValue
    }
    variant(fit: $fitKey, size: $sizeKey) {
      ...PdpServerSideSelectedVariantFields
    }
    variants {
      ...PdpServerSideVariantsFields
    }
  }
`)

export const pdpServerSideSelectedVariantFields = gql(`
  fragment PdpServerSideSelectedVariantFields on ProductCatalogVariant {
    __typename
    id
    sku
    upc
    availability
    shopifyId
    shopifyProductId
    urlPath
    price {
      amount
      currencyCode
    }
    compareAtPrice {
      amount
    }
    size {
      id
      displayValue
    }
    fit {
      id
      displayValue
    }
  }
`)

export const pdpServerSideVariantsFields = gql(`
  fragment PdpServerSideVariantsFields on ProductCatalogVariant {
    __typename
    id
    sku
    upc
    availability
    stockState
    shopifyId
    urlPath
    price {
      amount
      currencyCode
    }
    compareAtPrice {
      amount
    }
    size {
      id
    }
    fit {
      id
    }
  }
`)

const pdpServerSidePropsQuery = gql(`
  query GetPdpServerSideProps(
      $collectionHandle: String, 
      $productHandle: String!, 
      $colorKey: String!, 
      $fitKey: String!, 
      $sizeKey: String!, 
      $portalCollectionHandle: String,
      $isPortalCollectionHandleFilled: Boolean!
  ) {
    productCatalogStandardProduct(handle: $productHandle) {
      __typename
      id
      ... on ProductCatalogStandardProduct {
        ...PdpServerSideProductFields
      }
      variantGroup(color: $colorKey) {
        ...PdpServerSideVariantGroupFields
      }
    }
    productGroupView(handle: $productHandle) {
      __typename
      id
      sections {
        __typename
        ...PageBannerSectionFragment
        ...PageHeroSectionFragment
        ...PageTileSectionFragment
      }
      promotionalColoredTextSegments {
        ...ColoredTextSegmentFragment
      }
    }  
  }
`)

export type UsePdpServerSidePropsProps = {
  productHandle: string
  colorKey: string | undefined
  fitKey: string | undefined
  sizeKey: string | undefined
  collectionHandle: string | undefined
  includeUnavailable?: boolean
  isPreview?: boolean
  launchTags?: string
}

export type UsePdpServerSidePropsResponse =
  | {
      data: undefined
      status: 'idle' | 'rejected' | 'pending' | 'not-found'
    }
  | {
      data: {
        product: PdpServerSideProductFieldsFragment & {
          variantGroup: NonNullable<PdpServerSideVariantGroupFieldsFragment> & {
            isPortalColor: boolean
          }
        }
        productView: NonNullable<GetPdpServerSidePropsQuery['productGroupView']> | undefined
      }
      status: 'resolved' | 'pending'
    }

export const primeClientWithPdpServerSideProps = ({
  client,
  props,
}: {
  client: ApolloClient<NormalizedCacheObject>
  props: UsePdpServerSidePropsProps
}) => {
  const {
    productHandle,
    colorKey = '',
    fitKey = '',
    sizeKey = '',
    collectionHandle,
    includeUnavailable,
    isPreview,
    launchTags,
  } = props

  return client.query({
    errorPolicy: 'all',
    query: pdpServerSidePropsQuery,
    variables: {
      productHandle,
      colorKey,
      fitKey,
      sizeKey,
      collectionHandle,
      portalCollectionHandle: undefined,
      isPortalCollectionHandleFilled: false,
    },
    context: { includeUnavailable, isPreview, launchTags },
  })
}

function usePdpServerSidePropsImpl(
  props: UsePdpServerSidePropsProps | undefined
): UsePdpServerSidePropsResponse {
  const { portal } = usePortal()

  const {
    productHandle,
    colorKey = '',
    fitKey = '',
    sizeKey = '',
    collectionHandle,
    includeUnavailable,
    isPreview,
    launchTags,
  } = props!

  const {
    data: queryData,
    error,
    called,
    loading,
    previousData,
  } = useQuery(pdpServerSidePropsQuery, {
    variables: {
      productHandle,
      colorKey,
      fitKey,
      sizeKey,
      collectionHandle,
      portalCollectionHandle: portal?.collectionHandle,
      isPortalCollectionHandleFilled: !!portal?.collectionHandle,
    },
    context: { includeUnavailable, isPreview, launchTags },
  })

  const data = useMemo(() => {
    const data = loading ? previousData : queryData
    const variantGroup: PdpServerSideVariantGroupFieldsFragment | undefined =
      data?.productCatalogStandardProduct?.variantGroup ?? undefined
    const product: PdpServerSideProductFieldsFragment | undefined =
      data?.productCatalogStandardProduct ?? undefined

    const isPortalColor = !!(product?.portalOptions?.colors ?? []).find(
      colorOption => colorOption.color.id === variantGroup?.color?.id
    )

    return product && variantGroup
      ? {
          product: {
            ...product,
            variantGroup: {
              ...variantGroup,
              isPortalColor,
            },
          },
          productView: data?.productGroupView ?? undefined,
        }
      : undefined
  }, [loading, previousData, queryData])

  if (!called) {
    return {
      status: 'idle',
      data: undefined,
    }
  }

  if (loading) {
    return {
      status: 'pending',
      data,
    }
  }

  if (error || !data) {
    const notFoundError = error?.graphQLErrors.find(
      graphQLError => graphQLError.extensions?.errorType === 'NOT_FOUND'
    )
    return {
      status: notFoundError || !data ? 'not-found' : 'rejected',
      data: undefined,
    }
  }

  return {
    status: 'resolved',
    data,
  }
}

const PdpServerSidePropsContainer = createContainer(usePdpServerSidePropsImpl)
PdpServerSidePropsContainer.Provider.displayName = 'PdpServerSidePropsImpl'

export const PdpServerSidePropsProvider = ({
  initialState,
  children,
}: {
  initialState: UsePdpServerSidePropsProps
  children: React.ReactNode
}) => {
  return (
    <PdpServerSidePropsContainer.Provider initialState={initialState}>
      {children}
    </PdpServerSidePropsContainer.Provider>
  )
}

export const usePdpServerSideProps = PdpServerSidePropsContainer.useContainer
